import {
  Module,
  PATH_PROJECT_SETTLEMENTS,
  PATH_PROJECT_SETTLEMENTS_DETAILS,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY,
  PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS,
  PATH_ROOT,
  YM_DATE_FORMAT,
} from "@/constants";
import { useProjectBreadcrumb } from "@/modules/projects/hooks/useProjectBreadcrumb";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { ProjectService } from "@/modules/projects/service/project";
import { ReadProjectSettlements } from "@/schemas/projectSettlements";
import { BreadcrumbItem } from "@/types/breadcrumb";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { formatDate } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath } from "react-router-dom";

interface SettlementsDetailsBreadcrumbProps {
  projectId: string | undefined;
  settlementId?: string | undefined;
  settlementQuery: UseQueryResult<ReadProjectSettlements>;
}
interface GetPathProps {
  projectId: string | undefined;
  settlementId?: string | undefined;
}

const getPath = ({ projectId, settlementId }: GetPathProps) => {
  if (!settlementId) return undefined;
  return projectId
    ? generatePath(PATH_PROJECT_SETTLEMENTS_DETAILS, {
        projectId,
        settlementId,
      })
    : generatePath(PATH_PROJECTS_SETTLEMENTS_DIRECTORY_DETAILS, {
        settlementId,
      });
};

export const useSettlementsDetailsBreadcrumb = ({
  projectId,
  settlementId,
  settlementQuery,
}: SettlementsDetailsBreadcrumbProps): BreadcrumbItem[] => {
  const { t } = useTranslation();
  const routeProjectId = useProjectId();
  const isProjectIdPassedThroughRoutePath = Boolean(routeProjectId);

  const projectQuery = useQuery({
    queryKey: [Module.ProjectSettlements, projectId],
    queryFn: () => (projectId ? ProjectService.get(projectId) : null),
    enabled: Boolean(projectId),
  });

  const lastBreadcrumbItem = useMemo(() => {
    return {
      label:
        (settlementQuery.data &&
          formatDate(settlementQuery.data.period, YM_DATE_FORMAT)) ??
        "...",
      href: getPath({ projectId: routeProjectId, settlementId: settlementId }),
    };
  }, [settlementQuery.data?.period, routeProjectId, settlementId]);

  // When projectId can be found in the url,
  // then we are in a submodule of the project,
  // let's use the project breadcrumb
  if (isProjectIdPassedThroughRoutePath) {
    return useProjectBreadcrumb(projectQuery, [
      {
        label: t("projects.breadcrumb.projectSettlements"),
        href: projectId
          ? generatePath(PATH_PROJECT_SETTLEMENTS, { projectId })
          : undefined,
      },
      lastBreadcrumbItem,
    ]);
  }

  // Otherwise we are in the root of the application,
  // where the project settlements are a standalone module
  return [
    { label: t("breadcrumb.home"), href: PATH_ROOT },
    {
      label: t("projects.breadcrumb.projectSettlements"),
      href: PATH_PROJECTS_SETTLEMENTS_DIRECTORY,
    },
    lastBreadcrumbItem,
  ];
};
